import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';
import { ThemeProvider } from './contexts/ThemeContext';
import Login from './pages/Login';
import PINReset from './pages/PINReset';
import SetupPump from './pages/SetupPump';
import Dashboard from './pages/Dashboard';
import DailyOperations from './pages/DailyOperations';
import Accounts from './pages/Accounts';
import Reports from './pages/Reports';
import Alerts from './pages/Alerts';
import Settings from './pages/Settings';
import MasterAdminLogin from './pages/MasterAdminLogin';
import MasterAdminDashboard from './pages/MasterAdminDashboard';
import AuditLogs from './pages/AuditLogs';
import MultiStationDashboard from './pages/MultiStationDashboard';
import QuickActions from './pages/QuickActions';
import Payments from './pages/Payments';
import ReviewAccess from './pages/ReviewAccess';
import HelpPage from './pages/HelpPage';
import ECharging from './pages/ECharging';
import Lubricants from './pages/Lubricants';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthContext = React.createContext();
export const AccessContext = React.createContext();

function App() {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [accessSettings, setAccessSettings] = useState(null);

  // Fetch access settings when token is available
  useEffect(() => {
    if (token) {
      fetchAccessSettings();
    }
  }, [token]);

  const fetchAccessSettings = async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setAccessSettings({
        page_access: res.data.page_access || null,
        report_access: res.data.report_access || null
      });
    } catch (error) {
      console.error('Failed to fetch access settings');
    }
  };

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        setUser(JSON.parse(storedUser));
      }
    }
    setLoading(false);
  }, [token]);

  const login = (token, userData) => {
    setToken(token);
    setUser(userData);
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  return (
    <ThemeProvider>
      <AuthContext.Provider value={{ user, token, login, logout }}>
        <AccessContext.Provider value={{ accessSettings, refreshAccessSettings: fetchAccessSettings }}>
          <BrowserRouter>
            <Routes>
              {/* Master Admin Routes - Always accessible */}
              <Route path="/master-admin/login" element={<MasterAdminLogin />} />
              <Route path="/master-admin/dashboard" element={<MasterAdminDashboard />} />
              
              {/* Customer Routes */}
              <Route path="/setup" element={!user ? <SetupPump /> : <Navigate to="/" />} />
              <Route path="/login" element={!user ? <Login /> : <Navigate to="/" />} />
              <Route path="/reset-pin" element={user && user.is_first_login ? <PINReset /> : <Navigate to="/" />} />
              
              {/* Review Access Route - Always accessible */}
              <Route path="/review/:secret" element={<ReviewAccess />} />
              
              {user && !user.is_first_login ? (
                <>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/daily-operations" element={<DailyOperations />} />
                  <Route path="/quick-actions" element={<QuickActions />} />
                  <Route path="/accounts" element={<Accounts />} />
                  <Route path="/audit-logs" element={<AuditLogs />} />
                  <Route path="/reports" element={<Reports />} />
                  <Route path="/alerts" element={<Alerts />} />
                  <Route path="/settings" element={<Settings />} />
                  <Route path="/multi-station" element={<MultiStationDashboard />} />
                  <Route path="/payments" element={<Payments />} />
                  <Route path="/help" element={<HelpPage />} />
                  <Route path="/e-charging" element={<ECharging />} />
                  <Route path="/lubricants" element={<Lubricants />} />
                </>
              ) : (
                <Route path="*" element={<Navigate to={user ? "/reset-pin" : "/login"} />} />
              )}
            </Routes>
            <Toaster />
          </BrowserRouter>
        </AccessContext.Provider>
      </AuthContext.Provider>
    </ThemeProvider>
  );
}

export default App;
export { API };